/**
 * The village animals (สัตว์ในหมู่บ้าน): hens and their chicks, dogs, cats, ducks and the field buffalo, living by the
 * hour and the weather (systems/animals.ts). They sleep at night, go home out of the rain, lie in the sun at noon,
 * run from a fight, and the dogs follow or bark at the player by how the village sees them.
 *
 * The hens and cats of `CRITTERS` (data/folk.ts) are animals too: they keep their spawns there, and their thinking
 * moves here. The rest spawn from `ANIMAL_SPAWNS` below: at a landmark of the zone's map, or at a point of the
 * region map (`region`: the hub city's greybox, data/regions/yanyu.city.ts), only when the map reaches it.
 */
import type { CritterKind } from './folk';
import { START, type ZoneId } from './zones';

/** What an animal is doing: the renderer plays the motion of the same name. */
export type AnimalAct =
  'idle' | 'walk' | 'run' | 'eat' | 'scratch' | 'call' | 'sit' | 'sleep' | 'lie' | 'flee';

export const ANIMAL_ACTS: readonly AnimalAct[] = [
  'idle',
  'walk',
  'run',
  'eat',
  'scratch',
  'call',
  'sit',
  'sleep',
  'lie',
  'flee',
];

/** The kinds the animal rules drive (the sparrows of `CRITTERS` keep their own). */
export type AnimalKind = Exclude<CritterKind, 'bird'>;

export interface AnimalInfo {
  /** The card the figure shows past its model's distance: a 2D sprite set and its scale. */
  readonly card: { readonly sprite: string; readonly scale: number };
  /** Walking and running speeds (m/s). */
  readonly walk: number;
  readonly run: number;
  readonly radius: number;
  /** How far it roams from home (m). */
  readonly roam: number;
  /** Shies from a person this close (m); 0: never (dogs, the buffalo). */
  readonly shy: number;
  /** Runs from a fight this close (m). */
  readonly fight: number;
  /** Goes home out of the rain (ducks and the buffalo do not mind it). */
  readonly shelters: boolean;
  /** Up and about at night (cats), else asleep at home. */
  readonly nocturnal: boolean;
  /** Lies in the sun at noon. */
  readonly basks: boolean;
  /** How often it calls (cluck, bark, meow, quack, low): a mean gap in seconds. */
  readonly callEvery: number;
  /** Weights of its pastimes while out: eat, scratch, idle, walk. */
  readonly pastimes: readonly [eat: number, scratch: number, idle: number, walk: number];
}

export const ANIMALS: Readonly<Record<AnimalKind, AnimalInfo>> = {
  chicken: {
    card: { sprite: 'chicken', scale: 1 },
    walk: 0.7,
    run: 3.2,
    radius: 0.14,
    roam: 2.2,
    shy: 1.8,
    fight: 9,
    shelters: true,
    nocturnal: false,
    basks: false,
    callEvery: 22,
    pastimes: [5, 3, 2, 3],
  },
  chick: {
    card: { sprite: 'chicken', scale: 0.42 },
    walk: 0.6,
    run: 2.4,
    radius: 0.08,
    roam: 1.4,
    shy: 1.4,
    fight: 9,
    shelters: true,
    nocturnal: false,
    basks: false,
    callEvery: 14,
    pastimes: [4, 1, 2, 4],
  },
  duck: {
    card: { sprite: 'chicken', scale: 0.9 },
    walk: 0.6,
    run: 2.6,
    radius: 0.15,
    roam: 2.6,
    shy: 2.2,
    fight: 9,
    shelters: false,
    nocturnal: false,
    basks: false,
    callEvery: 18,
    pastimes: [5, 1, 3, 3],
  },
  cat: {
    card: { sprite: 'cat', scale: 1 },
    walk: 0.8,
    run: 3.6,
    radius: 0.14,
    roam: 3,
    shy: 0,
    fight: 12,
    shelters: true,
    nocturnal: true,
    basks: true,
    callEvery: 40,
    pastimes: [1, 2, 5, 3],
  },
  dog: {
    card: { sprite: 'wolf', scale: 0.62 },
    walk: 1.1,
    run: 4.2,
    radius: 0.2,
    roam: 4,
    shy: 0,
    fight: 7,
    shelters: true,
    nocturnal: false,
    basks: true,
    callEvery: 30,
    pastimes: [1, 2, 4, 4],
  },
  ox: {
    card: { sprite: 'boar', scale: 1.6 },
    walk: 0.7,
    run: 2.2,
    radius: 0.45,
    roam: 5,
    shy: 0,
    fight: 6,
    shelters: false,
    nocturnal: false,
    basks: false,
    callEvery: 60,
    pastimes: [6, 0, 3, 2],
  },
};

export interface AnimalSpawn {
  readonly zone: ZoneId;
  readonly kind: AnimalKind;
  /** A landmark of the zone's map (`plaza` is every map's plaza), shifted by `dx`, `dy` m … */
  readonly at?: string;
  readonly dx?: number;
  readonly dy?: number;
  /** … or a point of the region map (m), used only when the map reaches it (region maps on). */
  readonly region?: readonly [number, number];
  readonly n: number;
}

/** The hub city's frame (data/regions/yanyu.city.ts `CITY`): its centre and plaza. */
const CX = 1450;
const CY = 1650;

const BASE_SPAWNS: readonly AnimalSpawn[] = [
  // Yanyu village: chicks with the hens of the yards, dogs at the doors and the gate, ducks at the pier and the
  // pond, a buffalo on each field, a third cat by the houses.
  { zone: START, kind: 'chick', at: 'houseSW', dx: 2, dy: 1.5, n: 4 },
  { zone: START, kind: 'chick', at: 'home2', dx: -1, dy: 2, n: 3 },
  { zone: START, kind: 'dog', at: 'home1', dx: 1.5, dy: 1.5, n: 1 },
  { zone: START, kind: 'dog', at: 'houseSE', dx: -1.5, dy: 1.5, n: 1 },
  { zone: START, kind: 'dog', at: 'plaza', dx: -4, dy: 3, n: 1 },
  { zone: START, kind: 'cat', at: 'home3', dx: 1.5, dy: 1, n: 1 },
  { zone: START, kind: 'duck', at: 'ferry', dx: 1, dy: 1, n: 4 },
  { zone: START, kind: 'duck', at: 'pond', dx: 2, dy: 2, n: 3 },
  { zone: START, kind: 'ox', at: 'fieldsWest', dx: 0, dy: 2, n: 1 },
  { zone: START, kind: 'ox', at: 'fieldsEast', dx: 0, dy: 2, n: 1 },
  // The hub city (region maps): dogs and cats about the great plaza, hens in the south suburb's yards, ducks at
  // the park lake, a buffalo by the suburb's gardens.
  { zone: 'yanyu', kind: 'dog', region: [CX - 40, CY + 30], n: 1 },
  { zone: 'yanyu', kind: 'dog', region: [CX + 60, CY - 40], n: 1 },
  { zone: 'yanyu', kind: 'cat', region: [CX + 20, CY + 70], n: 2 },
  { zone: 'yanyu', kind: 'chicken', region: [1396, 2120], n: 4 },
  { zone: 'yanyu', kind: 'chick', region: [1396, 2120], n: 3 },
  { zone: 'yanyu', kind: 'chicken', region: [1514, 2120], n: 3 },
  { zone: 'yanyu', kind: 'duck', region: [1200, 1820], n: 4 },
  { zone: 'yanyu', kind: 'ox', region: [1460, 2140], n: 1 },
  // The plains town.
  { zone: 'plains', kind: 'dog', at: 'plaza', dx: -3, dy: 4, n: 1 },
  { zone: 'plains', kind: 'chick', at: 'house1', dx: 2, dy: 2, n: 3 },
];

/** The hub city's animals again on แคว้นเก้ากระบี่ (the region mirrored: y -> 3456 - y), keyed to the new Yanyu's zone. */
export const ANIMAL_SPAWNS: readonly AnimalSpawn[] = [
  ...BASE_SPAWNS,
  ...BASE_SPAWNS.filter((a) => a.region).map((a) => ({ ...a, zone: START, region: [a.region![0], 3456 - a.region![1]] as const })),
];

/** How the dogs take a player: their karma for now; the NPC-memory system's standing when it lands. */
export type DogTemper = 'friendly' | 'wary';
